import { createStore } from "solid-js/store"
import { probeHealth, reportMachineRouteSelection, type HealthProbeFailure, type MachineRouteReport, type HealthProbeResult, type MachineRoutePolicy, type PublicMachineRoute } from "@/transport/fleet"
import { stampRouteReport } from "./route-telemetry"
import { abortable } from "@/utils/abortable"
import { isRouteGatewayFailure } from "@/transport/route-errors"
import { machineOnThisDevice } from "@/storage/machine-locality"

const pageOnLoopback = () => typeof location !== "undefined" && ["localhost", "127.0.0.1", "[::1]"].includes(location.hostname)

export type MachineRouteKind = "direct-local" | "direct-public" | "unavailable"

/** Why this browser's last check of a machine's local connection failed. */
export type LocalRouteFailure = { reason: HealthProbeFailure["reason"]; http_status?: number }

export type MachineRouteInput = {
  name: string
  public_url: string
  public_routes?: PublicMachineRoute[]
  route_policy?: MachineRoutePolicy
  local_url?: string
  accessToken?: string
}

// A single-provider policy permits only that provider's public route: no
// loopback and no other provider, even during failures.
export function routePolicyProvider(machine: Pick<MachineRouteInput, "route_policy">): PublicMachineRoute["provider"] | undefined {
  if (machine.route_policy === "cloudflare-only") return "cloudflare"
  if (machine.route_policy === "opentunnel-only") return "opentunnel"
  return undefined
}

export type MachineRoute = {
  url: string
  kind: MachineRouteKind
  publicUrl: string
  localUrl?: string
  provider?: PublicMachineRoute["provider"]
  trigger: NonNullable<MachineRouteReport["trigger"]>
}

type RouteProbe = typeof probeHealth

type SelectOptions = {
  trigger?: MachineRouteReport["trigger"]
  avoidUrl?: string
  // Ranks this public route first, so selection leaves it only when it fails
  // its health check or answers well after another route.
  keepUrl?: string
  signal?: AbortSignal
  publish?: (route: MachineRoute) => void
  probe?: RouteProbe
  report?: (value: MachineRouteReport) => Promise<unknown> | unknown
  timeoutMs?: number
  localTimeoutMs?: number
}

function isLoopbackUrl(raw: string) {
  try {
    const url = new URL(raw)
    const host = url.hostname
    return url.protocol === "http:" && (host === "127.0.0.1" || host === "[::1]")
  } catch {
    return false
  }
}

export function machineDefaultRouteUrl(machine: MachineRouteInput) {
  const only = routePolicyProvider(machine)
  return (only
    ? machine.public_routes?.find(route => route.provider === only)?.url ?? ""
    : machine.public_url).replace(/\/+$/, "")
}

function normalizedInput(machine: MachineRouteInput) {
  return {
    publicUrl: machineDefaultRouteUrl(machine),
    localUrl: routePolicyProvider(machine) ? undefined : machine.local_url?.replace(/\/+$/, ""),
  }
}

function selectionKey(machine: MachineRouteInput) {
  const normalized = normalizedInput(machine)
  return [machine.name, normalized.publicUrl, normalized.localUrl ?? "", JSON.stringify(machine.public_routes ?? []), machine.route_policy ?? ""].join("\0")
}

// A probe's deadline counts time the page could run. A timer that fires well
// after it was due means the page was stalled, by a long task or a frozen or
// sleeping tab, and the answer may already be queued behind the stall; the
// deadline then gives it this long to arrive before calling the route dead.
const stalledProbeGraceMs = 1_000

function timeout(ms: number) {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout>
  const arm = (delay: number) => {
    const due = performance.now() + delay
    timer = setTimeout(() => {
      if (performance.now() - due > stalledProbeGraceMs) arm(stalledProbeGraceMs)
      else controller.abort(new DOMException("Route probe timed out", "TimeoutError"))
    }, delay)
  }
  arm(ms)
  return { controller, done: () => clearTimeout(timer) }
}

// The control plane lists public routes in the order browsers should prefer
// them. A route it does not list ranks after every listed one.
function controlPlaneRank(machine: MachineRouteInput, url: string) {
  const routes = machine.public_routes ?? []
  const index = routes.findIndex(route => route.url.replace(/\/+$/, "") === url.replace(/\/+$/, ""))
  return index < 0 ? routes.length : index
}

/**
 * When a lower-ranked route answers first, selection waits this long for a
 * better one. That covers a healthy preferred route's usual latency without
 * holding up startup when it is down.
 */
export const preferredRouteGraceMs = 300
/** While a page uses a lower-ranked route, it checks this often for a better one. */
export const preferredRouteCheckMs = 60_000

const trimUrl = (url: string) => url.replace(/\/+$/, "")

// Resolves with the best-ranked healthy route: at once when no better-ranked
// probe is still pending, otherwise after the grace period.
function preferredRoute(probes: Array<{ rank: number; result: Promise<MachineRoute | undefined> }>) {
  let timer: ReturnType<typeof setTimeout> | undefined
  let done = false
  const promise = new Promise<MachineRoute | undefined>((resolve) => {
    let best: { rank: number; route: MachineRoute } | undefined
    const pending = new Set(probes)
    const finish = (route?: MachineRoute) => {
      done = true
      clearTimeout(timer)
      resolve(route)
    }
    const check = () => {
      if (done) return
      if (best && ![...pending].some(probe => probe.rank < best!.rank)) finish(best.route)
      else if (!pending.size) finish(best?.route)
      else if (best && !timer) timer = setTimeout(() => finish(best!.route), preferredRouteGraceMs)
    }
    for (const probe of probes) void probe.result.then((route) => {
      pending.delete(probe)
      if (route && (!best || probe.rank < best.rank)) best = { rank: probe.rank, route }
      check()
    })
    check()
  })
  return { promise, cancel: () => { done = true; clearTimeout(timer) } }
}

// Upgrade selection only probes routes that would beat the working public one.
type Upgrade = "none" | "local-and-public" | "public-only"

// Public probes start short so a dead route is found fast. When every public
// probe of a selection times out, the client's own network is the likely cause,
// so each such selection doubles the next deadline, up to the cap.
const publicProbeTimeoutMs = 3_000
const slowPublicProbeTimeoutMs = 15_000

export function createMachineRouteManager(
  // Whether to probe a machine's loopback route. Chrome asks a public page for
  // local network access before its first loopback request, so a public page
  // probes only machines that opened the dashboard on this device; a page
  // served from loopback is never asked.
  mayProbeLoopback: (name: string) => boolean = (name) => pageOnLoopback() || machineOnThisDevice(name),
) {
  // Consecutive selections per machine whose public probes all timed out.
  const slowSelections = new Map<string, number>()
  // Locality outlives health failures and route metadata changes, not this manager's lifetime.
  const verifiedLocal = new Map<string, string>()
  // The public route this browser prefers for each machine, pinned or measured
  // fastest. It outranks the control plane's order but not loopback, and a
  // failing preferred route loses to any healthy one like any other route.
  const preferred = new Map<string, string>()
  // The last local check's failure per machine, so a fall back to a public
  // route can say why; cleared when the local connection answers.
  const [localFailures, setLocalFailures] = createStore<Record<string, LocalRouteFailure | undefined>>({})
  const publicRank = (machine: MachineRouteInput, url: string) =>
    trimUrl(url) === preferred.get(machine.name) ? -0.5 : controlPlaneRank(machine, url)
  const localOnlyURL = (machine: MachineRouteInput) => {
    if (routePolicyProvider(machine)) return undefined
    return verifiedLocal.get(machine.name)
  }
  const cache = new Map<string, {
    key: string
    route?: MachineRoute
    observed: MachineRoute
    retryAt: number
    accessToken?: string
  }>()
  type Subscriber = Pick<SelectOptions, "signal" | "publish">
  type Pending = { key: string; upgrade: Upgrade; avoidUrl?: string; promise: Promise<MachineRoute>; cancel: () => void; subscribers: Set<Subscriber> }
  const pending = new Map<string, Pending>()

  const subscribe = (name: string, entry: Pending, options?: SelectOptions) => {
    const subscriber: Subscriber = { signal: options?.signal, publish: options?.publish }
    entry.subscribers.add(subscriber)
    const cancel = () => {
      entry.subscribers.delete(subscriber)
      if (!entry.subscribers.size && pending.get(name) === entry) {
        pending.delete(name)
        entry.cancel()
      }
    }
    subscriber.signal?.addEventListener("abort", cancel, { once: true })
    if (subscriber.signal?.aborted) cancel()
    if (!subscriber.signal) return entry.promise
    return abortable(entry.promise, subscriber.signal).finally(() => {
      subscriber.signal?.removeEventListener("abort", cancel)
      entry.subscribers.delete(subscriber)
    })
  }

  const observed = (machine: MachineRouteInput) => {
    const key = selectionKey(machine)
    const cached = cache.get(machine.name)
    return cached?.key === key ? cached.observed : undefined
  }

  const peek = (machine: MachineRouteInput) => {
    const cached = cache.get(machine.name)
    return cached?.key === selectionKey(machine) ? cached.route : undefined
  }

  const select = (machine: MachineRouteInput, options?: SelectOptions, revalidate = false, upgrade: Upgrade = "none"): Promise<MachineRoute> => {
    if (options?.signal?.aborted) return Promise.reject(options.signal.reason)
    const reuse = (route: MachineRoute) => {
      options?.publish?.(route)
      return Promise.resolve(route)
    }
    const localUrl = localOnlyURL(machine) ?? normalizedInput(machine).localUrl
    if (!localUrl || !isLoopbackUrl(localUrl)) setLocalFailures(machine.name, undefined)
    const key = selectionKey(machine)
    const prior = cache.get(machine.name)
    const cached = peek(machine)
    const previous = prior?.key === key ? prior : undefined
    const trigger = options?.trigger ?? (prior?.key !== undefined && prior.key !== key ? "routes-changed" : prior ? "retry" : "initial")
    // Reuse failures for one base connection-retry interval; never add another sleep/backoff.
    if (previous?.observed.kind === "unavailable" && previous.accessToken === machine.accessToken && previous.retryAt > Date.now()) {
      return reuse(previous.observed)
    }
    if (cached && previous?.observed.kind !== "unavailable" && !revalidate) return reuse(cached)

    const existing = pending.get(machine.name)
    if (existing?.key === key && existing.upgrade === upgrade && (!options?.avoidUrl || existing.avoidUrl === options.avoidUrl)) return subscribe(machine.name, existing, options)

    existing?.cancel()
    const normalized = normalizedInput(machine)
    const localOnly = localOnlyURL(machine)
    // Advertised replacements are probe candidates, never request destinations
    // until their machine identity has been verified and published below.
    if (localOnly && (!normalized.localUrl || !isLoopbackUrl(normalized.localUrl))) normalized.localUrl = localOnly
    const candidates: Array<{ url: string; kind: MachineRouteReport["attempts"][number]["route"]; provider?: PublicMachineRoute["provider"] }> = []
    if (normalized.localUrl && isLoopbackUrl(normalized.localUrl) && (localOnly || mayProbeLoopback(machine.name))) {
      candidates.push({ url: normalized.localUrl, kind: "direct-local" })
    }
    if (normalized.publicUrl) candidates.push({ url: normalized.publicUrl, kind: "direct-public" })
    const only = routePolicyProvider(machine)
    for (const route of machine.public_routes ?? []) {
      if (only && route.provider !== only) continue
      const url = route.url.replace(/\/+$/, "")
      const existing = candidates.find(candidate => candidate.url === url)
      if (existing) existing.provider = route.provider
      else candidates.push({ url, kind: "direct-public", provider: route.provider })
    }

    const currentRank = upgrade !== "none" && cached ? publicRank(machine, cached.url) : Infinity
    // A quick health response is not evidence that this route can sustain a stream.
    const permitted = localOnly ? candidates.filter(candidate => candidate.kind === "direct-local")
      : upgrade === "none" ? candidates
      : candidates.filter(candidate => candidate.kind === "direct-local" ? upgrade === "local-and-public" : publicRank(machine, candidate.url) < currentRank)
    const routeTrigger = (kind: MachineRouteKind): MachineRoute["trigger"] => upgrade === "none" ? trigger
      : kind === "direct-local" ? "local-discovery" : "preferred-route"
    const alternatives = permitted.filter(candidate => candidate.url !== options?.avoidUrl)
    const eligible = options?.avoidUrl && alternatives.length ? alternatives : permitted
    const startedElapsed = Math.floor(performance.now())
    const check = options?.probe ?? probeHealth
    const reporter = options?.report ?? reportMachineRouteSelection
    const timeoutMs = options?.timeoutMs ??
      Math.min(publicProbeTimeoutMs * 2 ** (slowSelections.get(machine.name) ?? 0), slowPublicProbeTimeoutMs)
    const localTimeoutMs = options?.localTimeoutMs ?? 250
    const fallback: MachineRoute = {
      url: localOnly ?? normalized.publicUrl,
      kind: "unavailable",
      trigger,
      ...normalized,
    }

    const report = (value: MachineRouteReport) => {
      try {
        void Promise.resolve(reporter(stampRouteReport({ ...value, trigger: value.trigger ?? trigger, started_elapsed_ms: startedElapsed,
          previous: prior?.route?.kind === "unavailable" ? "none" : prior?.route?.kind ?? "none",
          ...(prior?.route?.provider ? { previous_provider: prior.route.provider } : {}),
        }))).catch(() => {})
      } catch {
        // Diagnostics must never affect machine connectivity.
      }
    }

    let transientFailure = false
    const selection = new AbortController()
    const promise = (async () => {
      const attempts: MachineRouteReport["attempts"] = []
      const probes = eligible.map((candidate) => {
        const deadline = timeout(candidate.kind === "direct-local" ? localTimeoutMs : timeoutMs)
        const abort = () => deadline.controller.abort(selection.signal.reason)
        selection.signal.addEventListener("abort", abort, { once: true })
        const result = (async () => {
          const started = performance.now()
          let result: HealthProbeResult = { healthy: false, reason: "network" }
          try {
            result = await abortable(check(candidate.url, {
              signal: deadline.controller.signal,
              accessToken: machine.accessToken,
              expectedMachineName: machine.name,
            }), deadline.controller.signal)
          } catch {
            if (deadline.controller.signal.aborted) result = {
              healthy: false,
              reason: deadline.controller.signal.reason?.name === "TimeoutError" ? "timeout" : "cancelled",
            }
          } finally {
            deadline.done()
            selection.signal.removeEventListener("abort", abort)
          }
          attempts.push({
            route: candidate.kind,
            ...(candidate.provider ? { provider: candidate.provider } : {}),
            result: result.healthy ? "success" : "unavailable",
            latency_ms: Math.max(0, Math.round(performance.now() - started)),
            ...(result.healthy === false ? { reason: result.reason, http_status: result.http_status } : {}),
          })
          return result.healthy ? { ...candidate, ...normalized, trigger: routeTrigger(candidate.kind) } : undefined
        })()
        const keepUrl = options?.keepUrl?.replace(/\/+$/, "")
        // A route kept through a stall outranks the preferred one: a slow handler is no reason to move.
        const rank = candidate.kind === "direct-local" ? -1 : candidate.url === keepUrl ? -0.75 : publicRank(machine, candidate.url)
        return { result, deadline, kind: candidate.kind, rank }
      })
      const publicRoute = preferredRoute(probes.filter(probe => probe.kind !== "direct-local"))
      try {
        // Warm public routes concurrently, but give verified loopback its bounded window.
        const localRoute = await probes.find(probe => probe.kind === "direct-local")?.result
        const localAttempt = attempts.find(attempt => attempt.route === "direct-local")
        if (localAttempt && localAttempt.reason !== "cancelled" && !selection.signal.aborted) {
          setLocalFailures(machine.name, localAttempt.result === "success" || !localAttempt.reason ? undefined
            : { reason: localAttempt.reason, ...(localAttempt.http_status ? { http_status: localAttempt.http_status } : {}) })
        }
        const route = localRoute ?? await publicRoute.promise
        if (selection.signal.aborted) return fallback
        if (route && route.kind !== "unavailable") {
          if (route.kind !== "direct-local") slowSelections.delete(machine.name)
          report({ machine: machine.name, selected: route.kind, trigger: route.trigger,
            ...(route.provider ? { selected_provider: route.provider } : {}), attempts: [...attempts] })
          return route
        }
        // Optional discovery says nothing about the health of the working public route.
        if (upgrade !== "none") return cached!
        if (attempts.length) report({ machine: machine.name, selected: "none", attempts: [...attempts] })
        const publicAttempts = attempts.filter((attempt) => attempt.route !== "direct-local")
        if (publicAttempts.length && publicAttempts.every((attempt) => attempt.reason === "timeout")) {
          slowSelections.set(machine.name, (slowSelections.get(machine.name) ?? 0) + 1)
        }
        transientFailure = attempts.every((attempt) => attempt.reason === "timeout" || attempt.reason === "network" ||
          (attempt.reason === "http" && isRouteGatewayFailure(attempt.http_status ?? 0)))
        return cached && transientFailure ? { ...cached, kind: "unavailable" as const, trigger } : fallback
      } finally {
        publicRoute.cancel()
        for (const probe of probes) {
          probe.deadline.controller.abort()
          probe.deadline.done()
        }
      }
    })().then((route) => {
      // Keep the last working address through a transient revalidation failure,
      // without reporting that failed probe as healthy to its caller.
      const entry = pending.get(machine.name)
      // An upgrade that found nothing better leaves the working route alone.
      if (upgrade !== "none" && route === cached) return route
      if (entry?.promise === promise && !selection.signal.aborted) {
        // Publish the cache and its consumers' transports in one synchronous turn.
        // Cancellation after this point cannot leave HTTP and sockets on different routes.
        pending.delete(machine.name)
        if (route.kind === "direct-local") verifiedLocal.set(machine.name, route.url)
        cache.set(machine.name, {
          key,
          route: route.kind !== "unavailable" ? route : transientFailure ? cached : undefined,
          observed: route,
          retryAt: route.kind === "unavailable" ? Date.now() + 1_000 : 0,
          accessToken: machine.accessToken,
        })
        for (const subscriber of entry.subscribers) {
          if (!subscriber.signal?.aborted) subscriber.publish?.(route)
        }
        entry.subscribers.clear()
      }
      return route
    }).finally(() => {
      if (pending.get(machine.name)?.promise === promise) pending.delete(machine.name)
    })

    const entry: Pending = { key, upgrade, avoidUrl: options?.avoidUrl, promise, cancel: () => selection.abort(), subscribers: new Set() }
    pending.set(machine.name, entry)
    return subscribe(machine.name, entry, options)
  }

  /** Sets the machine's preferred public route; reports whether it changed. Apply it with `reconsider`. */
  const prefer = (name: string, url: string | undefined) => {
    const next = url ? trimUrl(url) : undefined
    if (preferred.get(name) === next) return false
    if (next) preferred.set(name, next)
    else preferred.delete(name)
    return true
  }

  const invalidate = (name: string) => {
    cache.delete(name)
    verifiedLocal.delete(name)
    preferred.delete(name)
    setLocalFailures(name, undefined)
    pending.get(name)?.cancel()
    pending.delete(name)
  }

  const reselect = (machine: MachineRouteInput, options?: SelectOptions) => {
    return select(machine, options, true)
  }

  /**
   * Looks for a better route than a working public one: verified loopback
   * (unless `local` is false), then the preferred public route or ones the
   * control plane ranks higher. Finding none never disturbs the working route
   * or reports an outage.
   */
  const reconsider = (machine: MachineRouteInput, options?: SelectOptions & { local?: boolean }): Promise<MachineRoute | undefined> => {
    const cached = peek(machine)
    // Real failure recovery owns any concurrent selection; discovery must not cancel it.
    const selecting = pending.get(machine.name)
    if (cached?.kind !== "direct-public" || observed(machine)?.kind === "unavailable" ||
      (selecting && selecting.upgrade === "none")) return Promise.resolve(undefined)
    const localUrl = normalizedInput(machine).localUrl
    const local = options?.local !== false && !!localUrl && isLoopbackUrl(localUrl) && mayProbeLoopback(machine.name)
    const only = routePolicyProvider(machine)
    const better = (machine.public_routes ?? []).some(route => (!only || route.provider === only) &&
      publicRank(machine, route.url) < publicRank(machine, cached.url))
    if (!local && !better) return Promise.resolve(undefined)
    return select(machine, options, true, local ? "local-and-public" : "public-only")
  }

  const dispose = () => {
    for (const entry of pending.values()) entry.cancel()
    pending.clear()
    cache.clear()
    verifiedLocal.clear()
    preferred.clear()
    for (const name of Object.keys(localFailures)) setLocalFailures(name, undefined)
  }
  // Before selection verifies a route, requests go to the preferred one when
  // the machine still advertises it: it answered for this machine before.
  const preferredDestination = (machine: MachineRouteInput) => {
    const url = preferred.get(machine.name)
    const only = routePolicyProvider(machine)
    return machine.public_routes?.some(route => trimUrl(route.url) === url && (!only || route.provider === only)) ? url : undefined
  }
  const destination = (machine: MachineRouteInput) => localOnlyURL(machine) ?? peek(machine)?.url ?? preferredDestination(machine) ?? machineDefaultRouteUrl(machine)
  const preferredProvider = (machine: MachineRouteInput) => machine.public_routes?.find(route => trimUrl(route.url) === preferred.get(machine.name))?.provider
  const localFailure = (name: string) => localFailures[name]
  return { peek, observed, destination, localOnlyURL, localFailure, preferredProvider, select, reselect, reconsider, prefer, invalidate, dispose }
}

export const machineRouteManager = createMachineRouteManager()
